import axios from "axios";
import type { ApiResponse, Trip, Expense, Schedule, Settlement, User, Companion, Poll } from "./types";

const api = axios.create({
  baseURL: "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies for session-based auth
});

// Web session API (for Inertia pages)
const webApi = axios.create({
  baseURL: "/",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Request interceptor for auth token (API)
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // localStorage might not be available in certain contexts
    console.warn("localStorage not accessible", e);
  }
  // Add CSRF token from meta tag
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
  if (csrfToken) {
    config.headers["X-CSRF-Token"] = csrfToken;
  }
  return config;
});

// Request interceptor for CSRF token (Web)
webApi.interceptors.request.use((config) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
  if (csrfToken) {
    config.headers["X-CSRF-Token"] = csrfToken;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth API (Web Session-based)
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await webApi.post<ApiResponse<{ user: User; tokens: { access_token: string } }>>(
      "/session",
      { email, password }
    );
    return response.data;
  },

  signup: async (name: string, email: string, password: string) => {
    const response = await webApi.post<ApiResponse<{ user: User; tokens: { access_token: string } }>>(
      "/signup",
      { user: { name, email, password } }
    );
    return response.data;
  },

  logout: async () => {
    const response = await webApi.delete<ApiResponse<{ message: string }>>("/session");
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get<ApiResponse<User>>("/auth/current_user");
    return response.data;
  },
};

// Users API
export interface UserProfile {
  id: string;
  name: string;
  nickname: string;
  avatar_url: string | null;
  bio: string | null;
  is_following: boolean;
  stats: {
    trips_count: number;
    followers_count: number;
    following_count: number;
  };
  created_at: string;
}

export interface UserSummary {
  id: string;
  name: string;
  nickname: string;
  avatar_url: string | null;
}

export const usersApi = {
  // 내 프로필 조회
  getMe: async () => {
    const response = await api.get<ApiResponse<UserProfile>>("/users/me");
    return response.data;
  },

  // 내 프로필 수정
  updateMe: async (data: { name?: string; nickname?: string; bio?: string; avatar_url?: string }) => {
    const response = await api.patch<ApiResponse<UserProfile>>("/users/me", snakeCaseKeys(data));
    return response.data;
  },

  // 비밀번호 변경
  updatePassword: async (currentPassword: string, password: string, passwordConfirmation: string) => {
    const response = await api.patch<ApiResponse<{ message: string }>>("/users/me/password", {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    });
    return response.data;
  },

  // 공개 프로필 조회
  getProfile: async (id: string) => {
    const response = await api.get<ApiResponse<UserProfile>>(`/users/${id}`);
    return response.data;
  },

  // 팔로워 목록
  getFollowers: async (id: string, page = 1) => {
    const response = await api.get<ApiResponse<UserSummary[]>>(`/users/${id}/followers`, {
      params: { page },
    });
    return response.data;
  },

  // 팔로잉 목록
  getFollowing: async (id: string, page = 1) => {
    const response = await api.get<ApiResponse<UserSummary[]>>(`/users/${id}/following`, {
      params: { page },
    });
    return response.data;
  },

  // 팔로우
  follow: async (id: string) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/users/${id}/follow`);
    return response.data;
  },

  // 언팔로우
  unfollow: async (id: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/users/${id}/follow`);
    return response.data;
  },

  // 사용자 검색
  search: async (query: string) => {
    const response = await api.get<ApiResponse<UserSummary[]>>("/users/search", {
      params: { q: query },
    });
    return response.data;
  },

  // 공개 여행 목록
  getPublicTrips: async (id: string) => {
    const response = await api.get<ApiResponse<PublicTrip[]>>(`/users/${id}/trips`);
    return response.data;
  },
};

export interface PublicTrip {
  id: string;
  name: string;
  destination: string;
  country_code: string;
  start_date: string;
  end_date: string;
  duration_days: number;
  status: string;
}

// Trips API
export const tripsApi = {
  list: async (page = 1, perPage = 20) => {
    const response = await api.get<ApiResponse<Trip[]>>("/trips", {
      params: { page, per_page: perPage },
    });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<Trip>>(`/trips/${id}`);
    return response.data;
  },

  create: async (data: Partial<Trip>) => {
    const response = await api.post<ApiResponse<Trip>>("/trips", { trip: snakeCaseKeys(data) });
    return response.data;
  },

  update: async (id: string, data: Partial<Trip>) => {
    const response = await api.patch<ApiResponse<Trip>>(`/trips/${id}`, { trip: snakeCaseKeys(data) });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/trips/${id}`);
    return response.data;
  },

  // 동행자 목록 조회
  getCompanions: async (tripId: string) => {
    const response = await api.get<ApiResponse<Companion[]>>(`/trips/${tripId}/companions`);
    return response.data;
  },

  // 동행자 추가
  addCompanion: async (tripId: string, userId: string, role: string = "member") => {
    const response = await api.post<ApiResponse<Companion>>(`/trips/${tripId}/companions`, {
      user_id: userId,
      role,
    });
    return response.data;
  },

  // 동행자 제거
  removeCompanion: async (tripId: string, companionId: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/trips/${tripId}/companions/${companionId}`);
    return response.data;
  },

  // 공개 설정 토글
  togglePublic: async (tripId: string, isPublic: boolean) => {
    const response = await api.patch<ApiResponse<Trip>>(`/trips/${tripId}`, {
      trip: { is_public: isPublic },
    });
    return response.data;
  },
};

export const pollsApi = {
  list: async (tripId: string) => {
    const response = await api.get<ApiResponse<Poll[]>>(`/trips/${tripId}/polls`);
    return {
      ...response.data,
      data: response.data.data.map(normalizePoll),
    };
  },

  get: async (tripId: string, pollId: string) => {
    const response = await api.get<ApiResponse<Poll>>(`/trips/${tripId}/polls/${pollId}`);
    return {
      ...response.data,
      data: normalizePoll(response.data.data),
    };
  },

  create: async (tripId: string, data: Partial<Poll> & { options: Array<{ title: string; description?: string; position?: number }> }) => {
    const response = await api.post<ApiResponse<Poll>>(`/trips/${tripId}/polls`, {
      poll: {
        title: data.title,
        description: data.description,
        selection_type: data.selectionType,
        ends_at: data.endsAt,
        poll_options_attributes: data.options.map((option, index) => ({
          title: option.title,
          description: option.description,
          position: option.position ?? index,
        })),
      },
    });
    return {
      ...response.data,
      data: normalizePoll(response.data.data),
    };
  },

  vote: async (pollOptionId: string) => {
    const response = await api.post<ApiResponse<Poll>>(`/poll_options/${pollOptionId}/votes`);
    return {
      ...response.data,
      data: normalizePoll(response.data.data),
    };
  },

  removeVote: async (voteId: string) => {
    const response = await api.delete<ApiResponse<Poll>>(`/votes/${voteId}`);
    return {
      ...response.data,
      data: normalizePoll(response.data.data),
    };
  },

  close: async (tripId: string, pollId: string) => {
    const response = await api.post<ApiResponse<Poll>>(`/trips/${tripId}/polls/${pollId}/close`);
    return {
      ...response.data,
      data: normalizePoll(response.data.data),
    };
  },
};

// Expenses API
export const expensesApi = {
  list: async (tripId: string, page = 1, perPage = 20, category?: string) => {
    const response = await api.get<ApiResponse<Expense[]>>(`/trips/${tripId}/expenses`, {
      params: { page, per_page: perPage, category },
    });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<Expense>>(`/expenses/${id}`);
    return response.data;
  },

  create: async (tripId: string, data: Partial<Expense>) => {
    const response = await api.post<ApiResponse<Expense>>(`/trips/${tripId}/expenses`, {
      expense: snakeCaseKeys(data),
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Expense>) => {
    const response = await api.patch<ApiResponse<Expense>>(`/expenses/${id}`, { expense: snakeCaseKeys(data) });
    return response.data;
  },

  // 지출 참여자 업데이트
  updateParticipants: async (id: string, participantIds: string[]) => {
    const response = await api.patch<ApiResponse<Expense>>(`/expenses/${id}`, {
      expense: { participant_ids: participantIds },
    });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/expenses/${id}`);
    return response.data;
  },

  ocr: async (file: File) => {
    const formData = new FormData();
    formData.append("image", file);

    const response = await api.post<ApiResponse<{
      merchant_name: string;
      amount: number;
      currency: string;
      date: string;
      category: string;
    }>>("/expenses/ocr", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
};

// Schedules API
export const schedulesApi = {
  list: async (tripId: string, date?: string) => {
    const response = await api.get<ApiResponse<Schedule[]>>(`/trips/${tripId}/schedules`, {
      params: { date },
    });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<Schedule>>(`/schedules/${id}`);
    return response.data;
  },

  create: async (tripId: string, data: Partial<Schedule>) => {
    const response = await api.post<ApiResponse<Schedule>>(`/trips/${tripId}/schedules`, {
      schedule: snakeCaseKeys(data),
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Schedule>) => {
    const response = await api.patch<ApiResponse<Schedule>>(`/schedules/${id}`, { schedule: snakeCaseKeys(data) });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/schedules/${id}`);
    return response.data;
  },
};

// Settlement API
export const settlementApi = {
  get: async (tripId: string) => {
    const response = await api.get<ApiResponse<Settlement>>(`/trips/${tripId}/settlement`);
    return response.data;
  },

  calculate: async (tripId: string) => {
    const response = await api.post<ApiResponse<Settlement>>(`/trips/${tripId}/settlement/calculate`);
    return response.data;
  },

  complete: async (tripId: string) => {
    const response = await api.post<ApiResponse<{ message: string; settled_count: number }>>(
      `/trips/${tripId}/settlement/complete`
    );
    return response.data;
  },
};

// Utility function to convert camelCase to snake_case
function snakeCaseKeys(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      result[snakeKey] = obj[key];
    }
  }
  return result;
}

function normalizePoll(raw: any): Poll {
  return {
    id: raw.id,
    tripId: raw.tripId ?? raw.trip_id,
    title: raw.title,
    description: raw.description ?? null,
    status: raw.status,
    selectionType: raw.selectionType ?? raw.selection_type,
    endsAt: raw.endsAt ?? raw.ends_at ?? null,
    closedAt: raw.closedAt ?? raw.closed_at ?? null,
    createdAt: raw.createdAt ?? raw.created_at,
    createdBy: {
      id: raw.createdBy?.id ?? raw.created_by?.id,
      name: raw.createdBy?.name ?? raw.created_by?.name,
      avatarUrl: raw.createdBy?.avatarUrl ?? raw.createdBy?.avatar_url ?? raw.created_by?.avatarUrl ?? raw.created_by?.avatar_url ?? null,
    },
    options: (raw.options ?? []).map((option: any) => ({
      id: option.id,
      title: option.title,
      description: option.description ?? null,
      position: option.position,
      voteCount: option.voteCount ?? option.vote_count ?? 0,
      votes: option.votes?.map((vote: any) => ({
        id: vote.id,
        name: vote.name,
        avatarUrl: vote.avatarUrl ?? vote.avatar_url ?? null,
      })) ?? [],
    })),
    totalVotes: raw.totalVotes ?? raw.total_votes ?? 0,
    viewerVoteOptionIds: raw.viewerVoteOptionIds ?? raw.viewer_vote_option_ids ?? [],
  };
}

export default api;
